import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CalendarClock,
  Check,
  FileText,
  LoaderCircle,
  Plus,
  Search,
  X,
} from "lucide-react";
import {
  deadlineReminderTone,
  encodeCuijPath,
} from "@tribunalea/shared";
import {
  ArgentineDateInput,
  CopyableDate,
  EmptyState,
  PageHeader,
  useRouter,
} from "@tribunalea/ui";
import { useApi } from "../App.jsx";

function todayInMendoza() {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Argentina/Mendoza",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(new Date())
      .filter(({ type }) => type !== "literal")
      .map(({ type, value }) => [type, value]),
  );
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function formatDateOnly(value) {
  if (!value) return "Sin fecha";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}

function formatActivityDate(value) {
  if (!value) return "Sin actividad informada";
  return new Intl.DateTimeFormat("es-AR", {
    timeZone: "America/Argentina/Mendoza",
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(value));
}

export function FollowUpsPage() {
  const api = useApi();
  const { navigate } = useRouter();
  const today = useMemo(todayInMendoza, []);
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reminderDrafts, setReminderDrafts] = useState({});
  const [savingReminderFor, setSavingReminderFor] = useState("");
  const [completingReminder, setCompletingReminder] = useState("");
  const [openReminderForms, setOpenReminderForms] = useState([]);

  useEffect(() => {
    let active = true;
    api
      .listFollowUps()
      .then((result) => {
        if (active) setFollowUps(result);
      })
      .catch((requestError) => {
        if (active) setError(requestError.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [api]);

  const replaceFollowUp = (updated) => {
    setFollowUps((current) =>
      current
        .map((item) => (item.id === updated.id ? updated : item))
        .sort((left, right) => {
          if (left.nextReminder && right.nextReminder) {
            return left.nextReminder.dueDate.localeCompare(
              right.nextReminder.dueDate,
            );
          }
          if (left.nextReminder) return -1;
          if (right.nextReminder) return 1;
          return left.cuij.localeCompare(right.cuij);
        }),
    );
  };

  const updateDraft = (id, patch) => {
    setReminderDrafts((current) => ({
      ...current,
      [id]: {
        dueDate: current[id]?.dueDate || today,
        description: current[id]?.description || "",
        ...patch,
      },
    }));
  };

  const addReminder = async (event, followUp) => {
    event.preventDefault();
    const draft = reminderDrafts[followUp.id] || {
      dueDate: today,
      description: "",
    };
    if (!draft.dueDate || !draft.description.trim()) return;
    setSavingReminderFor(followUp.id);
    setError("");
    try {
      replaceFollowUp(
        await api.addFollowUpReminder(followUp.id, {
          dueDate: draft.dueDate,
          description: draft.description.trim(),
        }),
      );
      setReminderDrafts((current) => ({
        ...current,
        [followUp.id]: { dueDate: today, description: "" },
      }));
      setOpenReminderForms((current) =>
        current.filter((id) => id !== followUp.id),
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSavingReminderFor("");
    }
  };

  const completeReminder = async (followUp, reminder) => {
    setCompletingReminder(reminder.id);
    setError("");
    try {
      replaceFollowUp(
        await api.setFollowUpReminderStatus(
          followUp.id,
          reminder.id,
          "completed",
        ),
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setCompletingReminder("");
    }
  };

  return (
    <div className="page follow-ups-page">
      <PageHeader
        eyebrow="Praxis"
        title="Mis seguimientos"
        description="Organizá los expedientes que requieren atención y agregá todos los recordatorios que necesites."
      />

      {error && <div className="inline-error">{error}</div>}

      <div className="expedientes-section-heading follow-ups-section-heading">
        <div>
          <h2>Mis seguimientos</h2>
          <span>
            {followUps.length}{" "}
            {followUps.length === 1 ? "expediente" : "expedientes"}
          </span>
        </div>
        <p>Ordenados por el próximo recordatorio</p>
      </div>

      {loading ? (
        <div className="loading-list">
          <span />
          <span />
          <span />
        </div>
      ) : followUps.length ? (
        <div className="follow-ups-list expedientes-grid">
          {followUps.map((followUp) => {
            const nextReminder = followUp.nextReminder;
            const overdue = nextReminder?.dueDate < today;
            const reminderTone = nextReminder
              ? deadlineReminderTone(
                {
                  ...nextReminder,
                  status: overdue
                    ? "overdue"
                    : nextReminder.dueDate === today
                      ? "due_today"
                      : "upcoming",
                },
                { isUserParty: true },
              )
              : "none";
            const openReminders = followUp.reminders.filter(
              ({ status }) => status !== "completed",
            );
            const remainingReminders = Math.max(
              0,
              openReminders.length - 1,
            );
            const draft = reminderDrafts[followUp.id] || {
              dueDate: today,
              description: "",
            };
            return (
              <article
                className={`expediente-card follow-up-card-compact ${
                  nextReminder ? "has-reminder-tone" : ""
                } ${nextReminder ? `is-reminder-${reminderTone}` : ""}`}
                key={followUp.id}
              >
                <button
                  className="follow-up-card-main"
                  type="button"
                  onClick={() =>
                    navigate(
                      `/seguimientos/${encodeCuijPath(followUp.cuij)}`,
                    )
                  }
                >
                  <div className="expediente-card-top">
                    <span className="expediente-cuij">
                      {followUp.cuij}
                      {followUp.numero && (
                        <span className="expediente-number">
                          ({followUp.numero})
                        </span>
                      )}
                    </span>
                  </div>
                  <h3>{followUp.caratula || "Sin carátula informada"}</h3>
                  <span className="expediente-card-badges">
                    <span className="expediente-card-fuero">
                      {followUp.fuero || "Fuero no informado"}
                    </span>
                    <span className="expediente-card-activity-pill">
                      <Activity size={12} />
                      <CopyableDate
                        as="span"
                        value={followUp.fechaUltimoMovimiento}
                      >
                        {formatActivityDate(followUp.fechaUltimoMovimiento)}
                      </CopyableDate>
                    </span>
                  </span>
                </button>

                {nextReminder && (
                  <div className="follow-up-next-reminder">
                    <span>
                      {overdue ? (
                        <AlertTriangle size={14} />
                      ) : (
                        <CalendarClock size={14} />
                      )}
                    </span>
                    <div>
                      <small>{overdue ? "Vencido" : "Próximo"}</small>
                      <strong>{nextReminder.description}</strong>
                      <span>
                        <CopyableDate value={nextReminder.dueDate}>
                          {formatDateOnly(nextReminder.dueDate)}
                        </CopyableDate>
                        {remainingReminders > 0
                          ? ` · +${remainingReminders} más`
                          : ""}
                      </span>
                    </div>
                    <button
                      type="button"
                      title="Marcar como cumplido"
                      aria-label={`Marcar como cumplido: ${nextReminder.description}`}
                      disabled={completingReminder === nextReminder.id}
                      onClick={() =>
                        completeReminder(followUp, nextReminder)
                      }
                    >
                      {completingReminder === nextReminder.id ? (
                        <LoaderCircle className="spin" size={15} />
                      ) : (
                        <Check size={15} />
                      )}
                    </button>
                  </div>
                )}

                <div className="follow-up-reminders">
                  {openReminderForms.includes(followUp.id) ? (
                    <form
                      className="follow-up-reminder-form"
                      onSubmit={(event) => addReminder(event, followUp)}
                    >
                      <ArgentineDateInput
                        value={draft.dueDate}
                        onChange={(value) =>
                          updateDraft(followUp.id, { dueDate: value })
                        }
                      />
                      <label>
                        <FileText size={15} />
                        <input
                          autoFocus
                          value={draft.description}
                          maxLength={500}
                          placeholder="Descripción del recordatorio"
                          onChange={(event) =>
                            updateDraft(followUp.id, {
                              description: event.target.value,
                            })
                          }
                        />
                      </label>
                      <button
                        className="follow-up-cancel-reminder"
                        type="button"
                        aria-label="Cerrar formulario"
                        onClick={() =>
                          setOpenReminderForms((current) =>
                            current.filter((id) => id !== followUp.id),
                          )
                        }
                      >
                        <X size={15} />
                      </button>
                      <button
                        className="button secondary"
                        type="submit"
                        disabled={
                          savingReminderFor === followUp.id ||
                          !draft.dueDate ||
                          !draft.description.trim()
                        }
                      >
                        {savingReminderFor === followUp.id ? (
                          <LoaderCircle className="spin" size={15} />
                        ) : (
                          <Plus size={15} />
                        )}
                        Guardar
                      </button>
                    </form>
                  ) : (
                    <button
                      className="button secondary follow-up-show-reminder"
                      type="button"
                      onClick={() =>
                        setOpenReminderForms((current) => [
                          ...new Set([...current, followUp.id]),
                        ])
                      }
                    >
                      <Plus size={15} />
                      {nextReminder ? "Agregar otro" : "Agregar recordatorio"}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <EmptyState
          icon={CalendarClock}
          title="Todavía no tenés seguimientos"
          description="Buscá un expediente y agregalo a Mis seguimientos para comenzar."
          action={(
            <button
              className="button primary"
              type="button"
              onClick={() => navigate("/buscar-expedientes")}
            >
              <Search size={17} />
              Buscar expedientes
            </button>
          )}
        />
      )}
    </div>
  );
}
